import {
  faCircleDot,
  faClone,
  faEdit,
  faEye,
  faLink,
  faPaperPlane,
  faTableLayout,
} from '@fortawesome/pro-regular-svg-icons';
import { Box } from '@modusign/design-system';
import OutsideClickHandler from 'react-outside-click-handler';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { DropdownMenu, DropdownMenuRow } from '@app/components/Dropdown';
import useContractLinkPermission from '@app/features/contractLink/contractLinkManagement/hooks/useContractLinkPermission';
import { useDocumentPermission } from '@app/features/documents/hooks/useDocumentPermission';
import { useEditingDocumentPermission } from '@app/features/editingDocuments/hooks/useEditingDocumentPermission';
import { useDraftPermission } from '@app/features/form/hooks/useDraftPermission';
import useTemplatePermission from '@app/features/template/hooks/useTemplatePermission';
import useResponsiveValue from '@app/hooks/useResponsiveValue';
import { useCurrentUserMemberId } from '@app/hooks/workspace';
import pushDataLayer from '@app/libs/analytics/googleTagManager';
import { recordGTMEvent } from '@app/store/analytics/actions';

interface DraftsListItemDropdownProps {
  draftId: string;
  isOpen: boolean;
  onAddSignatureClick: () => void;
  onConvertToContractLinkClick?: () => void;
  onCreateTemplateClick?: () => void;
  onDraftCopy: () => void;
  onEditDraftClick: () => void;
  onRequestDocumentClick?: () => void;
  onToggleDropdown: () => void;
}

export default function DraftsDropdown({
  draftId,
  isOpen,
  onAddSignatureClick,
  onConvertToContractLinkClick,
  onCreateTemplateClick,
  onDraftCopy,
  onEditDraftClick,
  onRequestDocumentClick,
  onToggleDropdown,
}: DraftsListItemDropdownProps) {
  const dispatch = useDispatch();

  const { readablePermission, editablePermission } = useDraftPermission({
    draftId,
  });
  const { manageEditingDocumentPermission } = useEditingDocumentPermission();
  const { signingRequestablePermission } = useDocumentPermission();
  const { createTemplatePermission } = useTemplatePermission();
  const currentUserMemberId = useCurrentUserMemberId();
  const { managementPermission: manageContractLinkPermission } = useContractLinkPermission({
    requesterMemberId: currentUserMemberId,
  });

  const isDesktop = useResponsiveValue([false, false, true]);

  return (
    <Box
      bg="white"
      borderColor="tableBorderNeutralBase"
      borderStyle="solid"
      borderWidth={isOpen ? '0 1px' : undefined}
      boxShadow="0 40px 60px -30px rgb(0 0 0 / 30%)"
      left={0}
      maxHeight={isOpen ? '600px' : 0}
      overflow="hidden"
      position="absolute"
      sx={{
        transition: 'max-height 0.2s 0s cubic-bezier(0, 1, 0.5, 1), padding-bottom 0s 0.2s linear',
      }}
      top="100%"
      width="100%"
      zIndex={1}
    >
      <OutsideClickHandler disabled={!isOpen} onOutsideClick={onToggleDropdown}>
        <DropdownMenu>
          <Link style={{ display: 'block' }} to={`/draft-preview/${draftId}`}>
            <DropdownMenuRow
              icon={faEye}
              permission={readablePermission}
              title="서식 미리보기"
              onClick={() => dispatch(recordGTMEvent('draftReviewClick'))}
            />
          </Link>
          {isDesktop && (
            <DropdownMenuRow
              icon={faEdit}
              permission={editablePermission}
              title="내용 수정"
              onClick={onEditDraftClick}
            />
          )}
          <DropdownMenuRow
            icon={faClone}
            permission={editablePermission}
            title="복사본 만들기"
            onClick={onDraftCopy}
          />
          {isDesktop && (
            <DropdownMenuRow
              icon={faCircleDot}
              permission={manageEditingDocumentPermission}
              title="내 사인/도장 넣기"
              onClick={onAddSignatureClick}
            />
          )}
          {isDesktop && (
            <DropdownMenuRow
              icon={faPaperPlane}
              permission={signingRequestablePermission}
              title="서명 요청"
              onClick={onRequestDocumentClick}
            />
          )}
          {isDesktop && (
            <DropdownMenuRow
              icon={faTableLayout}
              permission={createTemplatePermission}
              title="템플릿으로 만들기"
              onClick={() => {
                if (onCreateTemplateClick) {
                  pushDataLayer({ event: 'clickCreateTemplate' });
                  onCreateTemplateClick();
                }
              }}
            />
          )}
          {isDesktop && (
            <DropdownMenuRow
              icon={faLink}
              permission={manageContractLinkPermission}
              title="링크서명 시작"
              onClick={() => {
                pushDataLayer({ event: 'clickCreateLinkSigning' });
                onConvertToContractLinkClick?.();
              }}
            />
          )}
        </DropdownMenu>
      </OutsideClickHandler>
    </Box>
  );
}
